import {
  CALENDAR_WORKING_HOURS,
  DEFAULT_APPOINTMENT_DURATION_MINUTES,
} from '@/constants/calendarConfiguration';

export function parseTimeToMinutes(time: string): number {
  const timeParts = time.split(':');
  if (timeParts.length < 2) {
    return 0;
  }
  const hours = Number(timeParts[0]);
  const minutes = Number(timeParts[1]);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return 0;
  }
  return hours * 60 + minutes;
}

export function computeAppointmentTopOffsetPixels(
  startTime: string,
  hourHeightPixels: number
): number {
  const firstHourInMinutes = CALENDAR_WORKING_HOURS.firstHour * 60;
  const startInMinutes = parseTimeToMinutes(startTime);
  return ((startInMinutes - firstHourInMinutes) / 60) * hourHeightPixels;
}

export function computeAppointmentHeightPixels(
  startTime: string,
  endTime: string,
  hourHeightPixels: number
): number {
  const startInMinutes = parseTimeToMinutes(startTime);
  const endInMinutes = parseTimeToMinutes(endTime);
  const durationInMinutes = endInMinutes - startInMinutes;
  if (endTime.length > 0 && durationInMinutes > 0) {
    return (durationInMinutes / 60) * hourHeightPixels;
  }
  return (DEFAULT_APPOINTMENT_DURATION_MINUTES / 60) * hourHeightPixels;
}

interface TimeSpannedAppointment {
  id: string;
  startTime: string;
  endTime: string;
}

export interface AppointmentColumnPlacement {
  columnIndex: number;
  columnCount: number;
}

function resolveEndMinutes(startTime: string, endTime: string): number {
  const startInMinutes = parseTimeToMinutes(startTime);
  const endInMinutes = parseTimeToMinutes(endTime);
  if (endTime.length > 0 && endInMinutes > startInMinutes) {
    return endInMinutes;
  }
  return startInMinutes + DEFAULT_APPOINTMENT_DURATION_MINUTES;
}

function assignColumnsWithinCluster(
  clusterAppointments: TimeSpannedAppointment[],
  placements: Record<string, AppointmentColumnPlacement>
): void {
  const columnEndMinutes: number[] = [];
  const columnIndexByAppointmentId: Record<string, number> = {};
  for (const appointment of clusterAppointments) {
    const startInMinutes = parseTimeToMinutes(appointment.startTime);
    let placedColumnIndex = -1;
    for (let candidateColumn = 0; candidateColumn < columnEndMinutes.length; candidateColumn++) {
      const candidateColumnEnd = columnEndMinutes[candidateColumn];
      if (candidateColumnEnd !== undefined && candidateColumnEnd <= startInMinutes) {
        placedColumnIndex = candidateColumn;
        break;
      }
    }
    if (placedColumnIndex === -1) {
      placedColumnIndex = columnEndMinutes.length;
      columnEndMinutes.push(0);
    }
    columnEndMinutes[placedColumnIndex] = resolveEndMinutes(
      appointment.startTime,
      appointment.endTime
    );
    columnIndexByAppointmentId[appointment.id] = placedColumnIndex;
  }
  const columnCount = columnEndMinutes.length;
  for (const appointment of clusterAppointments) {
    const columnIndex = columnIndexByAppointmentId[appointment.id];
    if (columnIndex !== undefined) {
      placements[appointment.id] = { columnIndex, columnCount };
    }
  }
}

export function assignAppointmentColumns(
  appointments: TimeSpannedAppointment[]
): Record<string, AppointmentColumnPlacement> {
  const sortedAppointments = [...appointments].sort(function compareBySpan(first, second) {
    const startDifference =
      parseTimeToMinutes(first.startTime) - parseTimeToMinutes(second.startTime);
    if (startDifference !== 0) {
      return startDifference;
    }
    return (
      resolveEndMinutes(first.startTime, first.endTime) -
      resolveEndMinutes(second.startTime, second.endTime)
    );
  });

  const placements: Record<string, AppointmentColumnPlacement> = {};
  let currentCluster: TimeSpannedAppointment[] = [];
  let currentClusterEndMinutes = 0;

  for (const appointment of sortedAppointments) {
    const startInMinutes = parseTimeToMinutes(appointment.startTime);
    if (currentCluster.length > 0 && startInMinutes >= currentClusterEndMinutes) {
      assignColumnsWithinCluster(currentCluster, placements);
      currentCluster = [];
      currentClusterEndMinutes = 0;
    }
    currentCluster.push(appointment);
    currentClusterEndMinutes = Math.max(
      currentClusterEndMinutes,
      resolveEndMinutes(appointment.startTime, appointment.endTime)
    );
  }
  assignColumnsWithinCluster(currentCluster, placements);

  return placements;
}
